'use client';

import { useLiveQuery } from '@sanity/preview-kit';
import { defaultLanguage } from '@stereoagency/constants';
import { researchGroupPageQuery, researchGroupProfilesQuery, researchGroupRelatedArticlesQuery, researchGroupRelatedJobsQuery, researchGroupRelatedProjectsQuery } from './researchGroupPageQuery';
import ResearchGroupPage from './ResearchGroupPage.client';
import { findInKeyArray } from '@/utils';
import { PageSections } from '@/components/layout';
import { diseasesForResearchGroupQuery } from './diseasesForResearchGroupQuery';

interface Props {
  id: string;
  lang: string;
}

export default function DraftModeResearchGroupPage({ id, lang }: Props) {
  const [researchGroup] = useLiveQuery<(typeof researchGroupPageQuery)[1] | null>(
    null,
    researchGroupPageQuery[0],
    { id, slug: null }
  );

  const [researchGroupProfiles] = useLiveQuery<(typeof researchGroupProfilesQuery)[1] | null>(
    null,
    researchGroupProfilesQuery[0],
    { id, slug: null }
  );

  const [researchGroupArticles] = useLiveQuery<(typeof researchGroupRelatedArticlesQuery)[1] | null>(
    null,
    researchGroupRelatedArticlesQuery[0],
    { id, slug: null }
  );

  const [researchGroupJobs] = useLiveQuery<(typeof researchGroupRelatedJobsQuery)[1] | null>(
    null,
    researchGroupRelatedJobsQuery[0],
    { id, slug: null }
  );

  const [researchGroupProjects] = useLiveQuery<(typeof researchGroupRelatedProjectsQuery)[1] | null>(
    null,
    researchGroupRelatedProjectsQuery[0],
    { id, slug: null }
  );

  const [diseases] = useLiveQuery<(typeof diseasesForResearchGroupQuery)[1] | null>(
    null,
    diseasesForResearchGroupQuery[0],
    { id, slug: null }
  );

  if (researchGroup == null) {
    return null;
  }

  const aboutContent = researchGroup.aboutContent
    ? findInKeyArray(researchGroup.aboutContent, [lang, defaultLanguage.id])
    : null;

  const researchContent = researchGroup.researchContent
    ? findInKeyArray(researchGroup.researchContent, [lang, defaultLanguage.id])
    : null;

  const bioContent = researchGroup.bioContent
    ? findInKeyArray(researchGroup.bioContent, [lang, defaultLanguage.id])
    : null;

  const teamContent = researchGroup.teamContent
    ? findInKeyArray(researchGroup.teamContent, [lang, defaultLanguage.id])
    : null;

  return (
    <ResearchGroupPage
      lang={lang}
      researchGroup={researchGroup}
      articles={researchGroupArticles?.articles}
      projects={researchGroupProjects?.projects}
      jobs={researchGroupJobs?.jobs}
      researchGroupLeaderProfile={researchGroupProfiles?.researchGroupLeader}
      departmentProfiles={researchGroupProfiles?.departmentProfiles}
      diseases={diseases ?? []}
      aboutContent={
        aboutContent?.value ? (
          <PageSections isPreview sections={aboutContent.value} />
        ) : null
      }
      researchContent={
        researchContent?.value ? (
          <PageSections isPreview sections={researchContent.value} />
        ) : null
      }
      bioContent={
        bioContent?.value ? (
          <PageSections isPreview sections={bioContent.value} />
        ) : null
      }
      teamContent={
        teamContent?.value ? (
          <PageSections isPreview sections={teamContent.value} />
        ) : null
      }
    />
  );
}
